import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart, 
  CheckCircle, 
  XCircle, 
  Clock, 
  TrendingUp,
  Users
} from 'lucide-react';

interface Product {
  id: string;
  status: string;
  seo_score: number | null;
  agent_id: string;
  rejection_reason: string | null;
}

interface AdminAnalyticsProps {
  products: Product[];
}

export const AdminAnalytics = ({ products }: AdminAnalyticsProps) => {
  const pendingCount = products.filter(p => p.status === 'pending').length;
  const approvedCount = products.filter(p => p.status === 'approved').length;
  const rejectedCount = products.filter(p => p.status === 'rejected').length;
  
  const avgSeoScore = products.length > 0 
    ? Math.round(products.reduce((acc, p) => acc + (p.seo_score || 0), 0) / products.length)
    : 0;

  // Get unique agents
  const uniqueAgents = new Set(products.map(p => p.agent_id)).size;

  // Calculate approval rate
  const totalReviewed = approvedCount + rejectedCount;
  const approvalRate = totalReviewed > 0 
    ? Math.round((approvedCount / totalReviewed) * 100)
    : 0;

  // Get rejection reasons breakdown
  const rejectionReasons = products
    .filter(p => p.rejection_reason)
    .reduce((acc, p) => {
      const reason = p.rejection_reason || 'Unknown';
      acc[reason] = (acc[reason] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingCount}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting admin review
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved Products</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{approvedCount}</div>
            <p className="text-xs text-muted-foreground">
              {approvalRate}% approval rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected Products</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rejectedCount}</div>
            <p className="text-xs text-muted-foreground">
              Need improvement
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg SEO Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgSeoScore}/100</div>
            <p className="text-xs text-muted-foreground">
              Across all products
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Agent Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Active Agents</span>
                <span className="font-semibold">{uniqueAgents}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total Uploads</span>
                <span className="font-semibold">{products.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Avg per Agent</span>
                <span className="font-semibold">
                  {uniqueAgents > 0 ? Math.round(products.length / uniqueAgents) : 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {Object.keys(rejectionReasons).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart className="h-5 w-5" />
                Rejection Reasons
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(rejectionReasons)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([reason, count]) => (
                    <div key={reason} className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {reason}
                      </span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
